import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * FT-3a parity — everything the separate editor could do to ONE room, staged as pending ops so it
 * rides the same single Save: the room information the grid has no column for, duplication onto N
 * rooms, and removal.
 */
const INFO_FIELDS = [
  ["building", "Building"], ["floor", "Floor"], ["area", "Area"], ["rr_type", "Bath type"],
  ["floor_plan", "Floor plan"], ["plan_sheet", "Enlarged floor plan"],
  ["elevation_sheet", "Elevations"], ["num_rooms", "#Units"], ["room_notes", "Room notes"],
];
const blankTarget = () => ({ room_number: "", room_name: "", room_type: "" });

export default function RoomActionsDialog({ room, roomRef, roomLabel, pending, isAdmin,
                                            products = 0, units = 0, onClose }) {
  const [targets, setTargets] = useState([blankTarget()]);

  const infoValue = (f) => {
    const staged = pending.get(`r:${room.key}:${f}`);
    return staged ? staged.op.fields[f] : (room[f] || "");
  };

  const stageInfo = (f, next) => {
    const key = `r:${room.key}:${f}`;
    if (next === (room[f] || "")) { pending.drop(key); return; }
    pending.stage(key, {
      op: { op: "set_room", room: roomRef, fields: { [f]: next } },
      room_label: roomLabel,
      label: `${f.replace(/_/g, " ")} “${room[f] || ""}” → “${next}”`,
    });
  };

  const stageDuplicate = () => {
    const rows = targets.filter((t) => t.room_number.trim());
    if (!rows.length) { toast.error("Give at least one new room a number"); return; }
    pending.stage(`dup:${room.key}:${Date.now()}`, {
      op: { op: "duplicate_room", room: roomRef, targets: rows },
      room_label: roomLabel,
      label: `duplicate onto ${rows.map((t) => t.room_number.trim()).join(", ")} `
        + `(${products} product(s), ${units} unit(s) each — no progress)`,
    });
    toast.success(`${rows.length} duplicate(s) waiting for Save`);
    setTargets([blankTarget()]);
    onClose();
  };

  const stageRemove = () => {
    const key = `del:${room.key}`;
    if (pending.has(key)) { pending.drop(key); return; }
    pending.stage(key, {
      op: { op: "remove_room", room: roomRef },
      room_label: roomLabel,
      label: `remove this room and its ${products} product row(s)`,
    });
    toast.info("Removal is waiting for Save — Discard puts it back");
    onClose();
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="frs-room-actions">
        <DialogHeader>
          <DialogTitle className="text-base">{roomLabel}</DialogTitle>
          <DialogDescription className="text-xs">
            {products} product(s) · {units} unit(s). Nothing is written until you press Save.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <section>
            <p className="pb-1 text-[10px] uppercase tracking-wide text-gray-500">
              Room information
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {INFO_FIELDS.map(([f, label]) => {
                const staged = pending.has(`r:${room.key}:${f}`);
                return (
                  <div key={f}>
                    <Label className="text-[10px] uppercase text-gray-500">{label}</Label>
                    <Input
                      value={infoValue(f)}
                      data-testid={`frs-room-field-${f}`}
                      onChange={(e) => stageInfo(f, e.target.value)}
                      className={`h-8 text-xs ${staged ? "ring-2 ring-amber-500 bg-amber-50" : ""}`}
                      placeholder="— blank —"
                    />
                  </div>
                );
              })}
            </div>
            <p className="pt-1 text-[11px] text-gray-500">
              Room #, room name and unit type are edited straight in the grid.
            </p>
          </section>

          <section className="rounded-lg border border-blue-200 bg-blue-50/60 p-2">
            <p className="text-xs font-semibold text-blue-900">
              Duplicate this room — products and quantities come along, progress and readiness do not
            </p>
            <div className="mt-1.5 space-y-1">
              {targets.map((t, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="w-5 text-[11px] text-gray-400">{i + 1}</span>
                  {[["room_number", "Room #"], ["room_name", "Room name"],
                    ["room_type", "Unit type"]].map(([f, ph]) => (
                    <Input key={f} placeholder={ph} value={t[f]} className="h-8 text-xs"
                           data-testid={`frs-dup-${f}-${i}`}
                           onChange={(e) => setTargets(targets.map((x, j) =>
                             (j === i ? { ...x, [f]: e.target.value } : x)))} />
                  ))}
                  {targets.length > 1 && (
                    <button type="button" data-testid={`frs-dup-drop-${i}`}
                            onClick={() => setTargets(targets.filter((_, j) => j !== i))}
                            className="text-gray-400 hover:text-red-600">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="frs-dup-add-row"
                      onClick={() => setTargets([...targets, blankTarget()])}>
                <Plus className="w-3 h-3 mr-1" /> another room
              </Button>
              <Button size="sm" className="h-7 text-xs" onClick={stageDuplicate}
                      data-testid="frs-dup-stage">
                <Copy className="w-3 h-3 mr-1" /> Add to pending
              </Button>
            </div>
          </section>

          {isAdmin && (
            <section className="rounded-lg border border-red-200 bg-red-50/60 p-2">
              <p className="text-xs font-semibold text-red-900">Remove this room</p>
              <p className="text-[11px] text-red-800">
                Only possible while nothing is installed in it. Its history is kept.
              </p>
              <Button size="sm" variant="outline" data-testid="frs-room-remove"
                      className="mt-1.5 h-7 text-xs border-red-300 text-red-700"
                      onClick={stageRemove}>
                <Trash2 className="w-3 h-3 mr-1" />
                {pending.has(`del:${room.key}`) ? "Keep the room" : "Remove on Save"}
              </Button>
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
